import React from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, GraduationCap, BookOpen, Briefcase } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const k12Calls = [
  "AI-Grounded Curriculum Co-Pilot for K-12 Educators",
  "Standards-Aligned Assessment Automation Platform",
  "Teacher Retention Analytics and Intervention System",
  "Evidence-Based Literacy Instruction Framework",
  "Parent-Community Engagement Digital Hub",
  "District-Level Data Dashboard for Student Outcomes",
  "Special Education IEP Workflow Automation",
  "Multilingual Classroom Support AI Tool",
  "School Safety Threat Assessment Platform",
  "SEL Integration Toolkit for Classroom Teachers",
];

const higherEdCalls = [
  "AI-Assisted Curriculum Modernization for Accreditation",
  "Student Success Predictive Analytics Engine",
  "Research Grant Management Optimization Platform",
  "Faculty Knowledge Retention System for Retiring Professors",
  "Technology Transfer Marketplace for University IP",
  "Micro-Credential and Stackable Certificate Platform",
  "Enrollment Yield Optimization Using Machine Learning",
  "Cross-Institutional Research Collaboration Hub",
  "Student Mental Health Early Warning System",
  "Open Educational Resources Curation Engine",
];

const workforceCalls = [
  "Industry-Aligned Competency Framework Builder",
  "Apprenticeship Management and Tracking Platform",
  "Employer Feedback Loop for Curriculum Co-Design",
  "Reskilling Pathway Recommendation Engine",
  "Portable Digital Credential Verification System",
  "Workforce Transition Planning for Displaced Workers",
  "CTE Program Outcome Measurement Dashboard",
  "AI-Powered Skills Gap Analysis Tool",
  "Earn-and-Learn Model Implementation Toolkit",
  "Regional Workforce Demand Forecasting System",
];

const EducationCallForSolutions = () => {
  const navigate = useNavigate();

  const handleCallClick = (callTitle: string, domain: string) => {
    navigate("/call-for-solutions/bounty", {
      state: {
        selectedArea: "Education Operations",
        chevronCall: {
          title: callTitle,
          domain,
          organization: "Education Channel",
          bountyAmount: "10000",
          bountyType: "Enterprise",
          contributors: ["Education Channel", "Giblet"],
          description: `Education ${domain} seeks innovative solutions for: ${callTitle}. This initiative aims to leverage best practices and emerging technologies to enhance educational outcomes, equity, and efficiency.`,
        },
      },
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <Link to="/education-channel">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Education Channel
            </Button>
          </Link>
          <h1 className="text-4xl font-bold text-foreground mb-4">Education Call for Solutions</h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Demonstration calls for solutions across education domains
          </p>
        </div>

        <Card className="mb-8">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <GraduationCap className="h-6 w-6" />
              K–12 Education
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {k12Calls.map((call, i) => (
                <Button key={i} variant="outline" className="h-auto p-4 text-left justify-start hover:bg-primary/5 text-foreground" onClick={() => handleCallClick(call, "K-12")}>
                  <span className="text-sm font-medium">{call}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <BookOpen className="h-6 w-6" />
              Higher Education
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {higherEdCalls.map((call, i) => (
                <Button key={i} variant="outline" className="h-auto p-4 text-left justify-start hover:bg-primary/5 text-foreground" onClick={() => handleCallClick(call, "Higher Education")}>
                  <span className="text-sm font-medium">{call}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Briefcase className="h-6 w-6" />
              Workforce Development & Career Pathways
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {workforceCalls.map((call, i) => (
                <Button key={i} variant="outline" className="h-auto p-4 text-left justify-start hover:bg-primary/5 text-foreground" onClick={() => handleCallClick(call, "Workforce")}>
                  <span className="text-sm font-medium">{call}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EducationCallForSolutions;
